import type { FC } from "react";
import { useContext } from "react";

import type { MinimumHeightProps, PushBlockContentWrapperProps } from "@ui/components/PushBlockContentWrapper";
import { PushBlockContentWrapper } from "@ui/components/PushBlockContentWrapper";
import type { LogoProps } from "@ui/eva-components/Logo";

import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { PushBlockContainerContext } from "../PushBlockContainer/PushBlockContainer";

import { PushBlockTextSection } from "./components";

export type PushBlockLogoProps = LogoProps & { isLogoEnabled?: boolean };

export type SecondButtonProps = {
  cta?: string;
  href?: string;
  isSecondButtonEnabled?: boolean;
};
export type ColumnWidthKeys = "3" | "4" | "6" | "12";

export type GridConfig = {
  columnDesktopWidth: ColumnWidthKeys;
  columnTabletWidth?: ColumnWidthKeys;
};

export type PushBlockProps = PushBlockContentWrapperProps & {
  containedCTA?: boolean;
  title: string;
  subtitle?: string;
  text?: string;
  cta?: string;
  href?: string;
  trackingEvent?: string;
  logoProps?: PushBlockLogoProps;
  isBackgroundOverlayEnabled?: boolean;
  isBackgroundOverlayEnabledOnMobile?: boolean;
  isFullHeightContent?: boolean;
  isFullWidthContent?: boolean;
  blockHeight?: MinimumHeightProps;
  secondButton?: SecondButtonProps;
  theme?: "dark" | "light" | "inherit";
  nativeBehaviour?: boolean;
  gridConfig?: GridConfig;
};

export const PushBlock: FC<PushBlockProps> = ({
  cta,
  href,
  text,
  title,
  subtitle,
  orientation,
  containedCTA,
  smallHeightOnMobile,
  logoProps,
  isBackgroundOverlayEnabled = false,
  isBackgroundOverlayEnabledOnMobile = false,
  isFullHeightContent = false,
  isFullWidthContent = false,
  blockHeight,
  secondButton,
  theme = "inherit",
  nativeBehaviour,
  gridConfig,
  imageFit,
  ...restProps
}) => {
  const { isCarousel } = useContext(PushBlockContainerContext);

  const isCentered = orientation === "centered";

  const dispatchGTMEvent = () => {
    dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: cta ?? "",
      cta_location: "push-block",
    });
  };

  return (
    <PushBlockContentWrapper
      {...restProps}
      smallHeightOnMobile={smallHeightOnMobile}
      isCarousel={isCarousel}
      orientation={orientation}
      isFullWidthContent={isFullWidthContent}
      isBackgroundOverlayEnabled={isBackgroundOverlayEnabled}
      isBackgroundOverlayEnabledOnMobile={isBackgroundOverlayEnabledOnMobile}
      blockHeight={blockHeight}
      theme={theme}
      imageFit={imageFit}
      gridConfig={gridConfig}
      renderContent={() => (
        <PushBlockTextSection
          cta={cta}
          href={href}
          text={text}
          title={title}
          subtitle={subtitle}
          isCentered={isCentered}
          onClick={dispatchGTMEvent}
          containedCTA={containedCTA}
          smallOnMobile={smallHeightOnMobile}
          logoProps={logoProps}
          isFullHeightContent={isFullHeightContent}
          isFullWidthContent={isFullWidthContent}
          secondButton={secondButton}
          nativeBehaviour={nativeBehaviour}
          gridConfig={gridConfig}
        />
      )}
    />
  );
};
